import { useState, useEffect } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { CredentialResponse } from '@react-oauth/google';
import { Card, CardContent } from '@/components/ui/Card';
import { GoogleAuthButton } from '@/components/ui/GoogleAuthButton';
import { useAuth } from '@/hooks';
import { useGlobalSettings } from '@/hooks/common/use-global-settings';
import { logger } from '@/utils/logger';

export const LoginPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const search = useSearch({ from: '/login' }) as { redirect?: string };

  const { handleGoogleAuth, handleEmailAuth } = useAuth();
  const { data: settings } = useGlobalSettings();

  const googleAuthAvailable = !!settings?.google_auth_key;
  const emailAuthAvailable = settings?.email_auth_enabled === true;

  // Trigger entrance animation
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const redirectAfterLogin = () => {
    const redirectTo = search.redirect && search.redirect.startsWith('/') && !search.redirect.startsWith('//') ? search.redirect : '/inventory';
    navigate({ to: redirectTo });
  };

  const handleGoogleLogin = async (response: CredentialResponse) => {
    try {
      setIsLoading(true);
      setError('');
      if (!response.credential) {
        setError('Google authentication failed. Please try again.');
        return;
      }
      await handleGoogleAuth({ credential: response.credential, select_by: response.select_by ?? '' });
      redirectAfterLogin();
    } catch (error) {
      setError('Google authentication failed. Please try again.');
      logger.error('Google auth error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError('');
      await handleEmailAuth({ email, password });
      redirectAfterLogin();
    } catch (error) {
      setError('Invalid email or password.');
      logger.error('Email auth error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className={`max-w-md w-full space-y-8 transition-all duration-700 ease-out ${
        isVisible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-8'
      }`}>
        <div className={`transition-all duration-500 delay-100 ${
          isVisible
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-4'
        }`}>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to LendRail
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Access your lending platform dashboard
          </p>
        </div>

        <div className={`transition-all duration-500 delay-200 ${
          isVisible
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-4'
        }`}>
          <Card>
            <CardContent className="p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="space-y-6">
                {googleAuthAvailable && (
                  <GoogleAuthButton
                    text="Sign in with Google"
                    loading={isLoading}
                    onSuccess={handleGoogleLogin}
                  />
                )}

                {emailAuthAvailable && (
                  <form onSubmit={handleEmailLogin} className="space-y-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                        Email
                      </label>
                      <input
                        id="email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="demo@lendrail.com"
                        disabled={isLoading}
                      />
                    </div>
                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                        Password
                      </label>
                      <input
                        id="password"
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        disabled={isLoading}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      ) : (
                        'Sign in'
                      )}
                    </button>
                  </form>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
